import { inject, Injectable } from "@angular/core";
import { FGDS_REGARD_3000_PLUS_MATERIAL_TYPE_MAP } from "@domain/product/fgds-product-type-resolver";
import { FgdsProductType, FullProductType, Product, RelationshipSubType } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { GasWarningCenterType } from "@project/gas-warning-center-configuration/gas-warning-center-type";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { forkJoin, map, Observable, of, shareReplay, switchMap } from "rxjs";

@Injectable()
export class Regard3000Service {
  private productService = inject(ProductService);
  private slotCoverByBaseId$ = new Map<string, Observable<Product | undefined>>();

  getModuleProducts(
    type: GasWarningCenterType = GasWarningCenterType.TYPES.find(({ key }) => key === "Regard_3000_plus")!,
  ): Observable<{
    inputModules: Product[];
    relayModules: Product[];
    gatewayModules: Product[];
  }> {
    return this.getBaseUnitProducts(type).pipe(
      switchMap((baseUnits) => {
        const relationships = baseUnits.flatMap((baseUnit) => baseUnit.relationships);
        const uniqueRelationships = [
          ...new Map(relationships.map((relationship) => [relationship.productId, relationship])).values(),
        ];

        if (!uniqueRelationships.length) {
          return of({ inputModules: [], relayModules: [], gatewayModules: [] });
        }

        return forkJoin(
          uniqueRelationships.map((relationship) =>
            this.productService.getProductById(relationship.productId).pipe(map((product) => ({ product, relationship }))),
          ),
        ).pipe(
          map((productRelationships) => ({
            inputModules: this.getProductsForModuleType(
              productRelationships,
              RelationshipSubType.INPUT,
              FullProductType.REGARD_3000_INPUT_ANALOG,
            ),
            relayModules: this.getProductsForModuleType(
              productRelationships,
              RelationshipSubType.RELAY,
              FullProductType.REGARD_3000_RELAIS,
            ),
            gatewayModules: this.getProductsForModuleType(
              productRelationships,
              RelationshipSubType.GATEWAY,
              FullProductType.REGARD_3000_GATEWAY,
            ),
          })),
        );
      }),
    );
  }

  getBaseUnitProducts(
    type: GasWarningCenterType = GasWarningCenterType.TYPES.find(({ key }) => key === "Regard_3000_plus")!,
  ): Observable<Product[]> {
    return this.getProductsByFullType(FullProductType.GASWARNINGCENTER_BASE).pipe(
      map((products) => this.filterBaseUnitsByType(products, type)),
    );
  }

  getLidUnitProducts(baseUnits: Product[]): Observable<Product[]> {
    if (!baseUnits.length) return of([]);

    const lidProductIds = new Set(
      baseUnits.flatMap((baseUnit) => baseUnit.relationships.map((relationship) => relationship.productId)),
    );
    return this.getProductsByFullType(FullProductType.REGARD_3000_DB, FgdsProductType.ATTACHMENT).pipe(
      map((products) => products.filter((product) => lidProductIds.has(product.id))),
    );
  }

  private getProductsByFullType(fullType: FullProductType, ...additionalProductTypes: FgdsProductType[]): Observable<Product[]> {
    return this.productService
      .getAvailableProducts(FgdsProductType.GASWARNINGCENTER, ...additionalProductTypes)
      .pipe(map((products) => products.filter((product) => this.matchesFullType(product, fullType))));
  }

  private filterBaseUnitsByType(products: Product[], type: GasWarningCenterType): Product[] {
    return products.filter((product) => {
      const productName = product.getLocalizedName("de-DE").toLocaleLowerCase();
      const matchesTypeName = productName.includes(type.name.toLocaleLowerCase());
      const isRegard3000Plus = type.key === "Regard_3000_plus";
      return matchesTypeName && (isRegard3000Plus || !productName.includes("plus"));
    });
  }

  getSlotCoverProduct(baseUnit: Product): Observable<Product | undefined> {
    if (!baseUnit?.id) throw new Error("Base unit id is required");

    const cached = this.slotCoverByBaseId$.get(baseUnit.id);
    if (cached) return cached;

    const stream$ = (
      baseUnit.relationships.length
        ? forkJoin(
            baseUnit.relationships.map((relationship) =>
              this.productService.getProductById(relationship.productId).pipe(map((product) => ({ product, relationship }))),
            ),
          ).pipe(
            map(
              (productRelationships) =>
                productRelationships.find(
                  ({ product, relationship }) =>
                    this.isUsableModuleProduct(product) &&
                    (this.matchesFullType(product, FullProductType.ATTACHMENT_OPTIONAL_SLOTCOVER) ||
                      this.matchesRelationshipSubType(relationship.relationshipSubType, RelationshipSubType.SLOT_COVER)),
                )?.product,
            ),
          )
        : of(undefined)
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.slotCoverByBaseId$.set(baseUnit.id, stream$);
    return stream$;
  }

  sumOfQuantity(products: ProductWithQuantity[]) {
    return products.reduce((sum, { quantity }) => {
      return sum + quantity;
    }, 0);
  }

  private getProductsForModuleType(
    productRelationships: { product: Product | undefined; relationship: Product["relationships"][number] }[],
    legacyRelationshipSubType: RelationshipSubType,
    fullType: FullProductType,
  ): Product[] {
    return productRelationships
      .filter(
        ({ product, relationship }) =>
          this.isUsableModuleProduct(product) &&
          (this.matchesRelationshipSubType(relationship.relationshipSubType, legacyRelationshipSubType) ||
            this.matchesFullType(product, fullType)),
      )
      .flatMap(({ product }) => (product ? [product] : []));
  }

  private isUsableModuleProduct(product: Product | undefined): product is Product {
    return (
      !!product &&
      (product.type === FgdsProductType.ATTACHMENT || product.type === FgdsProductType.GASWARNINGCENTER) &&
      product.isAvailable
    );
  }

  private matchesFullType(product: Product, fullType: FullProductType): boolean {
    return product.fullType === fullType || FGDS_REGARD_3000_PLUS_MATERIAL_TYPE_MAP.get(product.id) === fullType;
  }

  private matchesRelationshipSubType(
    relationshipSubType: RelationshipSubType | string | undefined,
    expectedSubType: RelationshipSubType,
  ): boolean {
    if (relationshipSubType === expectedSubType) return true;

    const legacySubTypes: Record<RelationshipSubType, string> = {
      [RelationshipSubType.INPUT]: "3000plus-input",
      [RelationshipSubType.SLOT_COVER]: "3000plus-slotcover",
      [RelationshipSubType.RELAY]: "3000plus-relais",
      [RelationshipSubType.GATEWAY]: "3000plus-gateway",
    };
    return relationshipSubType === legacySubTypes[expectedSubType];
  }
}
